import React, { FC, useEffect, useMemo, useState } from 'react';
import { Input, Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  CountryInput,
  LtrInput,
  DocumentInput,
  EmirateSelect,
  PhoneNumberFormItem,
  Select,
  Space,
  Checkbox,
  SpaceBetweenField,
} from '~/components';
import Form from '~/components/WizardForm';
import PinLocation from '~/components/PinLocation';
import features from '~/constants/features';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import Section from '~/layouts/Section ';
import { isAgent } from '~/utils/getDecodedJwt';
import CustomFields from '~/components/CustomFields';
import { useRefScrollTo } from '~/hooks/useRefScrollTo';
import { useGetDraft } from '~/controllers/draft';
import { capitalizeWords } from '~/utils/validateCharacters';
import { Draft } from '~/types/Draft';

const n = (namePath: string | string[]) => ['addresses'].concat([namePath].flat());

const Addresses: FC = () => {
  const ref = useRefScrollTo();
  const { t } = useTranslation('fillApplication');
  const [form] = Form.useForm();
  const draftQuery = useGetDraft<Draft<'addresses'>>();
  // const sameAsRegistered = form.getFieldValue('addresses')?.sameAsRegistered;
  const sameAsRegistered =
    draftQuery?.data?.addresses?.sameAsRegistered || form.getFieldValue('addresses')?.sameAsRegistered;
  const headOffice = draftQuery?.data?.addresses?.headOffice || form?.getFieldsValue().addresses?.headOffice;

  useEffect(() => {
    if (form && sameAsRegistered && draftQuery) {
      form.setFieldsValue({ addresses: { mailingAddress: headOffice } });
    }
  }, [form, sameAsRegistered, draftQuery, headOffice]);

  return (
    <Container className="Addresses" ref={ref}>
      <Form form={form} stepKey="addresses">
        <Typography.Title>{t('Address')}</Typography.Title>
        <Section formItemLast style={{ gap: 2 }}>
          <Typography.Title level={4}>{t('Registered address').toUpperCase()}</Typography.Title>
          <Typography.Paragraph>
            {t('This address must be identical to the one registered in the trade license certificate')}
          </Typography.Paragraph>
          <InputsGroupLayout>
            <Form.Item
              label={t('Country')}
              name={n(['headOffice', 'country'])}
              initialValue={features.defaultCountrySalesforce}
            >
              <CountryInput />
            </Form.Item>
            <Form.Item label={t('Emirate')} name={n(['headOffice', 'emirate'])}>
              <EmirateSelect />
            </Form.Item>
            <PhoneNumberFormItem
              label={capitalizeWords(t('Office phone number'))}
              name={n(['headOffice', 'phoneNumber'])}
            />
            <PhoneNumberFormItem
              initialValue=""
              label={capitalizeWords(t('Mobile number'))}
              name={n(['headOffice', 'mobileNumber'])}
              optional
            />
            <Form.Item initialValue="" label={t('P.O. Box')} name={n(['headOffice', 'poBox'])}>
              <Input />
            </Form.Item>
            <Form.Item label={t('Office / Shop no')} name={n(['headOffice', 'officeShopNo'])}>
              <Input />
            </Form.Item>
            <Form.Item label={t('Building name / No')} name={n(['headOffice', 'buildingNameNo'])}>
              <Input />
            </Form.Item>
            <Form.Item label={capitalizeWords(t('Street name'))} name={n(['headOffice', 'streetName'])}>
              <Input />
            </Form.Item>
            <Form.Item initialValue="" label={t('Landmark')} name={n(['headOffice', 'landmark'])}>
              <Input />
            </Form.Item>
            <Form.Item initialValue="" label={t('Website')} name={n(['headOffice', 'website'])}>
              <LtrInput />
            </Form.Item>
            <Form.Item label={t('Email')} name={n(['headOffice', 'email'])}>
              <LtrInput />
            </Form.Item>

            {/* <Space> */}
            {/* <Form.Item name={n(['headOffice', 'geolocation'])}> */}
            {/*  <Input /> */}
            {/* <PinLocation id="applicationPinLocation" /> */}
            {/* </Form.Item> */}
            {/* </Space> */}
          </InputsGroupLayout>
        </Section>
        <Section formItemLast>
          <Typography.Title level={4}>{t('Mailing address').toUpperCase()}</Typography.Title>
          <InputsGroupLayout>
            <Form.Item name={n(['sameAsRegistered'])} valuePropName="checked">
              <Checkbox>{t('Same as Registered Address')}</Checkbox>
            </Form.Item>
            <SpaceBetweenField />
            <Form.Item
              label={t('Country')}
              name={n(['mailingAddress', 'country'])}
              initialValue={features.defaultCountrySalesforce}
            >
              <CountryInput />
            </Form.Item>
            <Form.Item label={t('Emirate')} name={n(['mailingAddress', 'emirate'])}>
              <EmirateSelect disabled={sameAsRegistered} />
            </Form.Item>
            <PhoneNumberFormItem
              label={capitalizeWords(t('Mailling phone number'))}
              name={n(['mailingAddress', 'phoneNumber'])}
            />
            <PhoneNumberFormItem
              label={capitalizeWords(t('Mobile number'))}
              name={n(['mailingAddress', 'mobileNumber'])}
              disabled={sameAsRegistered}
              initialValue=""
              optional
            />
            <Form.Item initialValue="" label={t('P.O. Box')} name={n(['mailingAddress', 'poBox'])}>
              <Input disabled={sameAsRegistered} />
            </Form.Item>
            <Form.Item label={t('Office / Shop no')} name={n(['mailingAddress', 'officeShopNo'])}>
              <Input disabled={sameAsRegistered} />
            </Form.Item>
            <Form.Item label={t('Building name / No')} name={n(['mailingAddress', 'buildingNameNo'])}>
              <Input disabled={sameAsRegistered} />
            </Form.Item>
            <Form.Item label={capitalizeWords(t('Street name'))} name={n(['mailingAddress', 'streetName'])}>
              <Input disabled={sameAsRegistered} />
            </Form.Item>
            <Form.Item initialValue="" label={t('Landmark')} name={n(['mailingAddress', 'landmark'])}>
              <Input disabled={sameAsRegistered} />
            </Form.Item>
            <Form.Item initialValue="" label={t('Website')} name={n(['mailingAddress', 'website'])}>
              <LtrInput disabled={sameAsRegistered} />
            </Form.Item>
            <Form.Item label={t('Email')} name={n(['mailingAddress', 'email'])}>
              <Input disabled={sameAsRegistered} />
            </Form.Item>

            {/* <Form.Item name={n(['mailingAddress', 'geolocation'])}> */}
            {/*  <Input /> */}
            {/* <PinLocation id="applicationPinLocation" disabled={sameAsRegistered} /> */}
            {/* </Form.Item> */}
          </InputsGroupLayout>
        </Section>

        <CustomFields />
      </Form>
    </Container>
  );
};

const Container = styled.div`
  .Addresses-Checkbox {
    margin-bottom: 16px;
  }
`;

export default Addresses;
